'use client'
import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { AirportSelector } from './airportSelector'

interface AirportSelectorWrapperProps {
    type: 'from' | 'to'
    value: string
    loading: boolean
    options: any[]
    onSearch: (query: string, type: 'from' | 'to') => void
    onSelect: (value: string, type: 'from' | 'to') => void
    visible: boolean
    onClose: () => void
    isPopularAirports: boolean
}

const AirportSelectorWrapper: React.FC<AirportSelectorWrapperProps> = ({
    type,
    value,
    loading,
    options,
    onSearch,
    onSelect,
    visible,
    onClose,
    isPopularAirports
}) => {
    // State management
    const [searchQuery, setSearchQuery] = useState('')
    const timerRef = useRef<NodeJS.Timeout | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    const t = useTranslations('common')

    // Reset search when visibility changes
    useEffect(() => {
        if (visible) {
            // Focus the input when component becomes visible
            setTimeout(() => {
                inputRef.current?.focus()
            }, 100)
        } else {
            setSearchQuery('')
        }
    }, [visible])

    // Handle search with debouncing
    const handleSearchChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const query = e.target.value
            setSearchQuery(query)

            // Clear existing timer
            if (timerRef.current) {
                clearTimeout(timerRef.current)
            }

            // Debounce search

            timerRef.current = setTimeout(() => {
                onSearch(query.trim(), type)
            }, 300)
        },
        [onSearch, type]
    )

    // Handle option selection
    const handleOptionSelect = useCallback(
        (option: any) => {
            const selectedValue = `${option.payload.airportCode} - ${option.payload.cityName}, ${option.payload.countryCode}`
            onSelect(selectedValue, type)
            setSearchQuery('')
            onClose()
        },
        [onSelect, type, onClose]
    )

    // Handle clear search
    const handleClear = useCallback(() => {
        setSearchQuery('')
        inputRef.current?.focus()
    }, [])

    // Handle input focus
    const handleInputFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        e.target.select()
    }, [])

    // Computed values
    const title = type === 'from' ? t('searchOriginHeader') : t('searchDestinationHeader')
    const toFromValue = type === 'from' ? t('origin') : t('destination')
    const placeholder = `${t('searchPlaceholder', { toFrom: toFromValue })}`
    const showNoResults = !loading && options.length === 0

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current)
            }
        }
    }, [])

    if (!visible) return null

    return (
        <AirportSelector
            type={type}
            title={title}
            placeholder={placeholder}
            searchQuery={searchQuery}
            loading={loading}
            options={options}
            showNoResults={showNoResults}
            inputRef={inputRef}
            onSearchChange={handleSearchChange}
            onOptionSelect={handleOptionSelect}
            onClear={handleClear}
            onClose={onClose}
            onInputFocus={handleInputFocus}
            t={t}
            isPopularAirports={isPopularAirports}
        />
    )
}

export { AirportSelectorWrapper as AirportSelector }
export default AirportSelectorWrapper
